import { useState } from "react";
import { PlayIcon } from "lucide-react";
import { Button } from "@component/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@component/dialog";

export function TestAsEmployeeDialog({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void> | void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "We couldn't start your test session. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-6" closeButtonClassName="top-4 right-4">
        <DialogHeader className="flex flex-row items-center gap-3 text-left">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
            <PlayIcon className="h-5 w-5 text-blue-600" />
          </div>
          <DialogTitle className="text-lg font-semibold">
            Start a test session as yourself?
          </DialogTitle>
        </DialogHeader>

        <DialogDescription className="text-sm text-gray-700 space-y-1.5">
          <span className="block">
            You'll go through the same roleplay your team will see.
          </span>
          <span className="block">
            Test results are tagged{" "}
            <span className="font-medium text-amber-700">TEST</span> and don't
            count toward your reports or KPIs.
          </span>
          <span className="block text-xs text-gray-400 pt-0.5">
            The test link expires in 1 hour.
          </span>
        </DialogDescription>

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={submitting}>
            <PlayIcon className="w-4 h-4" />
            {submitting ? "Starting..." : "Start test roleplay"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
